import React, { Component } from 'react';
import { isMobile } from 'react-device-detect';

import ShimmeringPrism from './ShimmeringPrism';
import { prefersReducedMotion } from '../../../lib/motion';
import { getHomeSection, subscribeHomeSection } from '../../../lib/homeSection';
import { subscribeManifestoBackground } from '../../../lib/manifestoBackground';
import { getProjectColor, subscribeProjectColor } from '../../../lib/projectColor';
import classes from './ShimmeringBackground.module.css';

/**
 * Mobile background profile.
 *
 * The site no longer skips WebGL on mobile entirely. Instead, phones/coarse-pointer
 * devices mount a calmer, lower-resolution Shimmering Prism so the mobile site has
 * atmosphere instead of flat black. Desktop is unaffected (isMobileProfile === false →
 * the engine runs with its default options, byte-identical). A WebGL init failure or
 * reduced motion still degrades gracefully (try/catch → CSS radial fallback in the
 * module CSS; animate:false → a single static frame).
 *
 * Detection uses viewport + pointer media queries (not only the react-device-detect UA
 * flag), because DevTools emulation and hybrid devices report differently; `isMobile`
 * is folded in as an extra signal.
 */
function getMobileBackgroundProfile() {
    const mq = typeof window.matchMedia === 'function' ? (q) => window.matchMedia(q).matches : () => false;
    const isPhoneViewport = mq('(max-width: 719px)');
    const isCoarsePointer = mq('(pointer: coarse)');
    const isMobileProfile = isPhoneViewport || isCoarsePointer || isMobile;
    return {
        isMobileProfile,
        isPhoneViewport,
        // Phones get the lowest, tightest range; wide-mobile / coarse-pointer get a middle range.
        quality: isPhoneViewport ? 0.5 : 0.75,
        qualityMin: isPhoneViewport ? 0.35 : 0.5,
        qualityMax: isPhoneViewport ? 0.65 : 1.0,
    };
}

// Home section index for the projects/WORK preset — index 1 maps to the OPENED "deep" form in the
// engine (presetKeyForSection). Project-detail routes (/work/:slug) hold the background here so they
// inherit the opened WORK atmosphere instead of collapsing to the closed Hero sphere when Home
// unmounts (Home publishes setHomeSection(null) on unmount, which otherwise drives the engine home).
const WORK_DETAIL_SECTION = 1;

/**
 * ShimmeringBackground
 *
 * React wrapper that mounts the Shimmering Prism WebGL engine into a fixed,
 * full-screen, non-interactive layer behind all site content.
 *
 * Safety gates:
 *   - Mobile (viewport/pointer/UA): a reduced-resolution, visually calmer profile
 *     is mounted (see getMobileBackgroundProfile) — atmosphere without the full
 *     desktop GPU cost. WebGL init is wrapped in try/catch → CSS radial fallback.
 *   - Reduced motion: the engine renders a single static frame (no RAF loop),
 *     so the shader is effectively frozen (mobile and desktop alike).
 *   - Desktop with motion allowed: full animated background.
 *
 * The engine owns its own canvas, RAF loop, resize handling and full WebGL
 * disposal; this wrapper only manages create-on-mount / dispose-on-unmount.
 */
class ShimmeringBackground extends Component {
    constructor(props) {
        super(props);
        this.mount = React.createRef();
        this.engine = null;
        this.debugPanel = null;
        this.unsubSection = null;
        this.unsubManifesto = null;
        this.unsubProjectColor = null;
    }

    componentDidMount() {
        if (!this.mount.current) return;

        // `?debug` in the URL opens the live tweak panel (see ./debug.js). It also forces the
        // animation loop on so sliders have visible effect even under reduced motion.
        const debug = new URLSearchParams(window.location.search).has('debug');

        // Engine options. Mobile gets a calmer, lower-resolution profile; desktop keeps the
        // defaults (byte-identical). Reduced motion → animate:false (a single static frame) on both.
        const profile = getMobileBackgroundProfile();
        const options = { animate: debug ? true : !prefersReducedMotion() };
        if (profile.isMobileProfile) {
            options.mobile = true;
            options.quality = profile.quality;
            options.qualityMin = profile.qualityMin;
            options.qualityMax = profile.qualityMax;
        }

        // This layer mounts at the app root, so a WebGL init failure (headless, no-GPU
        // VM, iOS in an iframe, blocked/limited context) must never crash the site — fall
        // back to the CSS radial fallback (module CSS), exactly like a missing context.
        try {
            this.engine = new ShimmeringPrism(this.mount.current, options);
            this.engine.start();
        } catch (err) {
            if (this.engine) this.engine.dispose();
            this.engine = null;
            console.warn('ShimmeringBackground: WebGL unavailable, using plain background.', err);
            return;
        }

        // React to homepage section changes: crossfade the background to per-section presets.
        // The engine lives outside the component tree, so it can't use SketchContext / useHomeSection
        // — it subscribes to the same homeSection pub/sub that Home publishes to.
        // Initialise the section preset route-aware: on a project-detail route hold the opened WORK
        // preset (so a direct load / refresh of /work/:slug starts toward the opened state, not the
        // closed Hero sphere); otherwise follow the published home section.
        this.engine.setSection(this._isWorkDetailRoute() ? WORK_DETAIL_SECTION : getHomeSection());
        this.unsubSection = subscribeHomeSection((section) => {
            if (!this.engine) return;
            // On a project-detail route the background holds the opened WORK preset (asserted from the
            // pathname prop in componentDidUpdate). Ignore Home's section publishes here — notably the
            // setHomeSection(null) Home fires on unmount when WORK → detail navigates — so the
            // atmosphere does not collapse back to the closed Hero sphere during the transition.
            if (this._isWorkDetailRoute()) return;
            this.engine.setSection(section);
        });

        // Manifesto ritual (hero hold): freeze + shake during the hold, explode on completion.
        // Same out-of-tree pub/sub pattern as homeSection — driven by HomeHero/Home.
        this.unsubManifesto = subscribeManifestoBackground({
            onHold: (progress) => { if (this.engine) this.engine.setHold(progress); },
            onManifesto: (active) => { if (this.engine) this.engine.setManifestoActive(active); },
        });

        // Per-project glow tint (PROJECTS hover). Persistent: seed from the last value (e.g. after the
        // engine re-mounts) without a tween, then tween on each subsequent change. Published by WorkSelection.
        const seedColor = getProjectColor();
        if (seedColor) this.engine.setLightColor(seedColor, 0);
        this.unsubProjectColor = subscribeProjectColor((hex) => {
            if (this.engine) this.engine.setLightColor(hex);
        });

        if (debug && this.engine) {
            // Lazy + code-split: the panel is only fetched when ?debug is present.
            import('./debug')
                .then(({ createDebugPanel }) => {
                    // Guard against unmount during the async import.
                    if (this.engine) this.debugPanel = createDebugPanel(this.engine.params);
                })
                .catch((err) => console.warn('ShimmeringBackground: debug panel failed to load.', err));
        }
    }

    // True while a project-detail route (/work/:slug) is active. Reads the live path (prop first,
    // window.location as a fallback) so the homeSection subscription can correctly ignore Home's
    // unmount reset at the exact moment it fires (before componentDidUpdate runs).
    _isWorkDetailRoute() {
        const path = this.props.pathname || (typeof window !== 'undefined' ? window.location.pathname : '') || '';
        return path.startsWith('/work/');
    }

    componentDidUpdate(prevProps) {
        if (!this.engine) return;
        if (this.props.pathname === prevProps.pathname) return;
        // Entering (or moving between) /work/:slug — assert the opened WORK preset so detail inherits
        // the WORK atmosphere. setSection dedupes by preset key, so arriving from the WORK section
        // (already "deep") is a no-op → no closed-sphere flash. On leaving back to '/', do nothing:
        // Home remounts and republishes its section (returnToSection), so the engine stays on the
        // opened preset until that (usually identical) value arrives — again no flash.
        if (this._isWorkDetailRoute()) {
            this.engine.setSection(WORK_DETAIL_SECTION);
        }
    }

    componentWillUnmount() {
        if (this.unsubSection) {
            this.unsubSection();
            this.unsubSection = null;
        }
        if (this.unsubManifesto) {
            this.unsubManifesto();
            this.unsubManifesto = null;
        }
        if (this.unsubProjectColor) {
            this.unsubProjectColor();
            this.unsubProjectColor = null;
        }
        if (this.debugPanel && this.debugPanel.parentNode) {
            this.debugPanel.parentNode.removeChild(this.debugPanel);
        }
        this.debugPanel = null;
        if (this.engine) {
            this.engine.dispose();
            this.engine = null;
        }
    }

    render() {
        // aria-hidden + pointer-events:none (CSS): purely decorative, never blocks input.
        return (
            <div
                ref={this.mount}
                className={classes.ShimmerBackground}
                aria-hidden="true"
            />
        );
    }
}

export default ShimmeringBackground;
